import { BhapticsContent } from "@/store/reducers/contents";
import { PageSeoData } from "@/types/seo";
import { SEO_CONSTANTS, buildBreadcrumb } from "../constants";

/**
 * HTML 태그 제거 및 텍스트 정리
 */
function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, "") // HTML 태그 제거
    .replace(/&nbsp;/g, " ") // &nbsp; 처리
    .replace(/&amp;/g, "&") // &amp; 처리
    .replace(/&lt;/g, "<") // &lt; 처리
    .replace(/&gt;/g, ">") // &gt; 처리
    .replace(/\s+/g, " ") // 연속 공백 정리
    .trim();
}

/**
 * gameResell이 있는 게임용 Product JSON-LD 생성
 */
function buildProductJsonLd(
  game: BhapticsContent,
  cleanDescription: string,
  imageUrl: string,
  gameUrl: string,
) {
  const gameResell = game.gameResell!;
  const usdPrice = gameResell.platformList
    ?.flatMap((p) => p.priceList)
    .find((p) => p.currency === "USD");

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: game.name,
    description: cleanDescription || null,
    image: imageUrl,
    brand: {
      "@type": "Brand",
      name: game.developer || game.publisher || "bHaptics",
    },
    ...(usdPrice && {
      offers: {
        "@type": "Offer",
        price:
          usdPrice.discount > 0
            ? (usdPrice.price - usdPrice.discount).toFixed(2)
            : usdPrice.price.toFixed(2),
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: gameUrl,
        seller: {
          "@type": "Organization",
          name: "bHaptics",
        },
      },
    }),
    ...(game.avgRatingPoint > 0 && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: game.avgRatingPoint.toFixed(1),
        ratingCount: game.reviewCount,
        bestRating: 5,
        worstRating: 1,
      },
    }),
  };
}

/**
 * gameResell이 없는 일반 게임용 SoftwareApplication JSON-LD 생성
 */
function buildSoftwareApplicationJsonLd(
  game: BhapticsContent,
  cleanDescription: string,
  imageUrl: string,
  platforms: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: game.name,
    description: cleanDescription || null,
    image: imageUrl,
    applicationCategory: "Game",
    operatingSystem: platforms || null,
    ...((game.developer || game.publisher) && {
      author: {
        "@type": "Organization",
        name: game.developer || game.publisher,
      },
    }),
    ...(game.avgRatingPoint > 0 && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: game.avgRatingPoint.toFixed(1),
        ratingCount: game.reviewCount,
        bestRating: 5,
        worstRating: 1,
      },
    }),
  };
}

/**
 * 게임 상세 페이지 SEO 생성기
 */
export function generateGameSeo(
  game: BhapticsContent,
  locale: string,
): PageSeoData {
  const supportedDevices = game.compatibleDevices?.slice(0, 3).join(", ") || "";
  const platforms =
    game.supportedPlatforms
      ?.filter((p) => p.enabled)
      .map((p) => p.platform)
      .slice(0, 3)
      .join(", ") || "";

  // HTML 태그 제거 후 160자로 자르기
  const cleanDescription = stripHtml(game.description);
  const description =
    cleanDescription.slice(0, 160) ||
    `Experience ${game.name} with immersive haptic feedback on bHaptics TactSuit.${supportedDevices ? ` Supports ${supportedDevices}.` : ""}`;

  const imageUrl =
    game.thumbnailWideLink ||
    game.thumbnailLink ||
    `${SEO_CONSTANTS.BASE_URL}${SEO_CONSTANTS.DEFAULT_OG_IMAGE}`;

  const isResellable =
    game.gameResell?.enable && game.gameResell.platformList?.length > 0;

  // 끝 슬래시 — `trailingSlash: true` 라 없으면 구조화 데이터의 url 이 308 을 한 번 더 탄다(`docs/pitfalls.md` #60 끝슬래시)
  const gameUrl = `${SEO_CONSTANTS.BASE_URL}/${locale}/games/experiences/${game.slug}/`;
  const mainJsonLd = isResellable
    ? buildProductJsonLd(game, cleanDescription, imageUrl, gameUrl)
    : buildSoftwareApplicationJsonLd(
        game,
        cleanDescription,
        imageUrl,
        platforms,
      );

  return {
    title: `${game.name} - Haptic Feedback Experience | bHaptics`,
    description,
    ogImage:
      game.thumbnailWideLink ||
      game.thumbnailLink ||
      SEO_CONSTANTS.DEFAULT_OG_IMAGE,
    ogType: isResellable ? "product" : "website",
    canonicalPath: `/games/experiences/${game.slug}`,
    keywords: [
      game.name,
      "haptic feedback",
      "bHaptics",
      "VR",
      ...game.categories,
      ...(game.compatibleDevices || []),
    ],
    jsonLd: [
      mainJsonLd,
      buildBreadcrumb(locale, [
        { name: "Home", path: "" },
        // ★`/games` 는 `→ /games/pc/` 리다이렉트 스텁이다 (#5683 · #5795). 브레드크럼은
        //   **절대 URL 을 크롤러에게 직접 준다** — `<Link>` 가 아니라 정규화를 못 받으므로
        //   여기 적은 값이 그대로 색인된다(prod 실측 2026-09-17:
        //   `https://www.bhaptics.com/ko/games` → 홉 2 → `/ko/games/pc/`).
        { name: "Games", path: "/games/pc" },
        { name: game.name, path: `/games/experiences/${game.slug}` },
      ]),
    ],
  };
}
